import { usePostHog } from "posthog-js/react";

/**
 * Analytics Event Types
 *
 * Type-safe event definitions for PostHog tracking.
 * Add new events here to ensure type safety across the app.
 */
type AnalyticsEvent =
  | {
      name: "demo_flow_step_completed";
      properties: { step: number; agent_type?: string };
    }
  | {
      name: "demo_flow_scan_submitted";
      properties: { plugins_count: number; strategies_count: number };
    }
  | {
      name: "demo_flow_abandoned";
      properties: { step: number; reason?: string };
    }
  | {
      name: "scan_results_viewed";
      properties: { scan_uuid: string };
    }
  | {
      name: "scan_results_shared";
      properties: { scan_uuid: string };
    }
  | {
      name: "filter_applied";
      properties: { filter_type: string; value: string };
    }
  | {
      name: "tour_started";
      properties: { tour_name: string };
    }
  | {
      name: "tour_step_viewed";
      properties: { tour_name: string; step: string; step_index?: number };
    }
  | {
      name: "tour_completed";
      properties: { tour_name: string };
    }
  | {
      name: "tour_skipped";
      properties: { tour_name: string };
    };

/**
 * useAnalytics Hook
 *
 * Provides type-safe event tracking for PostHog analytics.
 *
 * @example
 * ```tsx
 * const { track } = useAnalytics();
 * track({ name: 'demo_flow_step_completed', properties: { step: 2, agent_type: 'our-ai' } });
 * ```
 */
export function useAnalytics() {
  const posthog = usePostHog();

  const track = <T extends AnalyticsEvent>(event: T) => {
    posthog?.capture(event.name, event.properties);
  };

  return { track, posthog };
}
